import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { formatDistanceToNowStrict } from "date-fns";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
	getPeopleAlsoViewedByPersonId,
	PeopleAlsoViewed,
	PeopleAlsoViewedDirection,
} from "../../api/person/people-also-viewed";

interface PersonPeopleAlsoViewedProps {
	personId: number;
	direction?: PeopleAlsoViewedDirection;
}

const TITLES: Record<PeopleAlsoViewedDirection, string> = {
	outgoing: "PeopleAlsoViewed",
	incoming: "PeopleAlsoViewed (backward)",
};

const SUBTITLES: Record<PeopleAlsoViewedDirection, string> = {
	outgoing:
		'People LinkedIn suggests when viewing this profile ("People you may know")',
	incoming: "Profiles where LinkedIn suggested this person as a related profile",
};

export const PersonPeopleAlsoViewed = ({
	personId,
	direction = "outgoing",
}: PersonPeopleAlsoViewedProps) => {
	const [recommendations, setRecommendations] = useState<PeopleAlsoViewed[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!personId) return;

		getPeopleAlsoViewedByPersonId(personId, direction)
			.then((response) => {
				setRecommendations(response.results);
				setLoading(false);
			})
			.catch((err) => {
				setError(err.message);
				setLoading(false);
			});
	}, [personId, direction]);

	const title = TITLES[direction];
	const subtitle = SUBTITLES[direction];
	const dataComponent = `people-also-viewed-accordion-${direction}`;

	if (loading) {
		return (
			<Accordion defaultExpanded={false} data-component={dataComponent}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="accordion-summary"
				>
					<Typography variant="h6">{title}</Typography>
				</AccordionSummary>
				<AccordionDetails data-component="accordion-details">
					<Box display="flex" justifyContent="center" alignItems="center">
						<CircularProgress />
					</Box>
				</AccordionDetails>
			</Accordion>
		);
	}

	if (error) {
		return (
			<Accordion defaultExpanded={false} data-component={dataComponent}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="accordion-summary"
				>
					<Typography variant="h6">{title}</Typography>
				</AccordionSummary>
				<AccordionDetails data-component="accordion-details">
					<Alert severity="error">{error}</Alert>
				</AccordionDetails>
			</Accordion>
		);
	}

	if (recommendations.length === 0) {
		return (
			<Accordion defaultExpanded={false} data-component={dataComponent}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="accordion-summary"
				>
					<Typography variant="h6">{title} (0)</Typography>
				</AccordionSummary>
				<AccordionDetails data-component="accordion-details">
					<Typography variant="body2" color="text.secondary">
						No PeopleAlsoViewed data available for this person.
					</Typography>
				</AccordionDetails>
			</Accordion>
		);
	}

	return (
		<Accordion defaultExpanded={false} data-component={dataComponent}>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				data-component="accordion-summary"
			>
				<Typography variant="h6">
					{title} ({recommendations.length})
				</Typography>
			</AccordionSummary>
			<AccordionDetails data-component="accordion-details">
				<Typography
					variant="body2"
					color="text.secondary"
					gutterBottom
					data-component="people-also-viewed-subtitle"
				>
					{subtitle}
				</Typography>
				<List dense data-component="people-also-viewed-list">
					{recommendations.map((rec) => {
						const other =
							direction === "incoming" ? rec.person_from : rec.person_to;
						return (
							<ListItem
								key={rec.id}
								data-component="people-also-viewed-list-item"
								sx={{
									borderBottom: 1,
									borderColor: "divider",
									"&:last-child": { borderBottom: 0 },
									padding: 0,
								}}
							>
								<Link
									component={RouterLink}
									to={`/person/${other.id}`}
									underline="none"
									color="inherit"
									sx={{
										display: "block",
										width: "100%",
										padding: "8px 16px",
										"&:hover": {
											backgroundColor: "action.hover",
										},
									}}
									data-component="people-also-viewed-link"
								>
									<ListItemText
										data-component="people-also-viewed-list-item-text"
										primary={
											<Typography
												variant="body1"
												data-component="people-also-viewed-name"
											>
												{other.name}
											</Typography>
										}
										secondary={
											<Box data-component="people-also-viewed-meta-box">
												{other.headline && (
													<Typography
														variant="body2"
														color="text.secondary"
														data-component="people-also-viewed-headline"
													>
														{other.headline}
													</Typography>
												)}
												<Typography
													variant="caption"
													display="block"
													data-component="people-also-viewed-timestamp"
												>
													Extracted{" "}
													{formatDistanceToNowStrict(
														new Date(rec.created)
													)}{" "}
													ago
												</Typography>
											</Box>
										}
										slotProps={{ secondary: { component: "div" } }}
									/>
								</Link>
							</ListItem>
						);
					})}
				</List>
			</AccordionDetails>
		</Accordion>
	);
};
